import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import immigrationConsultancyService from '../services/immigrationConsultancyService';

const VisaDetailsPage = () => {
  const { slug } = useParams();
  const [visa, setVisa] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [relatedPolicies, setRelatedPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadVisaDetails = useCallback(async () => {
    try {
      setLoading(true);
      const visaData = await immigrationConsultancyService.getVisaType(slug);
      setVisa(visaData);
      
      if (visaData?.id) {
        const requirementsData = await immigrationConsultancyService.getVisaRequirements(visaData.id);
        setRequirements(requirementsData);
        // Also load latest related country policies as guidance
        const policies = await immigrationConsultancyService.getPolicies({ country: visaData.country_code, recent: true });
        setRelatedPolicies(policies || []);
      }
    } catch (error) {
      console.error('Error loading visa details:', error);
      setError('Failed to load visa details');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadVisaDetails();
  }, [loadVisaDetails]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !visa) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Visa Not Found</h1>
          <p className="text-gray-600 mb-8">The visa you're looking for doesn't exist or has been removed.</p>
          <Link 
            to="/visa" 
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Visa Information
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 via-blue-700/90 to-indigo-800/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
              <span className="text-white/90 text-sm font-medium tracking-wide uppercase">
                {visa.country_name} • {visa.category.toUpperCase()}
              </span>
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {visa.name}
            </h1>
            <p className="text-xl text-blue-50 max-w-3xl mx-auto mb-8 leading-relaxed">
              {visa.description}
            </p>
            <Link
              to="/visa"
              className="inline-flex items-center px-6 py-3 bg-white text-blue-600 rounded-full font-semibold hover:bg-blue-50 transition-all duration-300"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to All Visas
            </Link>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Visa Image */}
            <div className="relative h-64 rounded-3xl overflow-hidden mb-8 shadow-2xl">
                <img
                  src={immigrationConsultancyService.getVisaImage(visa.category, visa.country_code)}
                alt={`${visa.name} - ${visa.country_name}`}
                className="w-full h-full object-cover"
                  onError={(e) => immigrationConsultancyService.handleImageError(e, visa.country_code)}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <h2 className="text-2xl font-bold text-white mb-2">About This Visa</h2>
                <p className="text-blue-100 leading-relaxed">{visa.description}</p>
              </div>
            </div>

            {/* Requirements Section (structured list) */}
            {requirements.length > 0 && (
              <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Requirements</h3>
                <div className="space-y-4">
                  {requirements.map((req, index) => (
                    <div key={req.id} className="flex items-start p-4 bg-gray-50 rounded-2xl border border-gray-100">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                        <span className="text-blue-600 font-bold text-sm">{index + 1}</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-2">{req.title}</h4>
                        <p className="text-gray-600 leading-relaxed">{req.description}</p>
                        {req.is_mandatory && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-red-100 text-red-800 text-xs font-medium mt-2">
                            Required
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Requirements Overview (fallback to text field) */}
            {!requirements.length && visa?.requirements && (
              <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Requirements Overview</h3>
                <div className="prose max-w-none">
                  <div className="text-gray-700 leading-relaxed whitespace-pre-line">{visa.requirements}</div>
                </div>
              </div>
            )}

            {/* Latest Policy Guidance (by country) */}
            {relatedPolicies && relatedPolicies.length > 0 && (
              <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Latest Policy Guidance</h3>
                <div className="space-y-6">
                  {relatedPolicies.slice(0, 1).map((p) => (
                    <div key={p.id} className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                      <div className="mb-2 text-sm text-gray-500">{p.country_name} • {new Date(p.effective_date).toLocaleDateString()}</div>
                      <h4 className="font-semibold text-gray-900 mb-2">{p.title}</h4>
                      <div className="text-gray-700 leading-relaxed whitespace-pre-line">{p.content || p.summary}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Key Information Card */}
            <div className="bg-white rounded-3xl shadow-xl p-8 mb-8 sticky top-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Key Information</h3>
              
              <div className="space-y-6">
                {visa.cost && (
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-2xl border border-green-100">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">💰</span>
                      <span className="font-semibold text-gray-700">Cost</span>
                    </div>
                    <span className="font-bold text-green-600 text-lg">{visa.cost}</span>
                  </div>
                )}
                
                {visa.processing_time && (
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-2xl border border-blue-100">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">⏰</span>
                      <span className="font-semibold text-gray-700">Processing Time</span>
                    </div>
                    <span className="font-bold text-blue-600 text-lg">{visa.processing_time}</span>
                  </div>
                )}
                
                {visa.validity_period && (
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-2xl border border-purple-100">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">📅</span>
                      <span className="font-semibold text-gray-700">Validity</span>
                    </div>
                    <span className="font-bold text-purple-600 text-lg">{visa.validity_period}</span>
                  </div>
                )}
              </div>

              {/* Source Information */}
              <div className="mt-6 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                <h4 className="text-sm font-bold text-gray-700 mb-2">Information Source</h4>
                <p className="text-sm text-gray-600">
                  This information is compiled from official government sources and immigration authorities.
                </p>
                <div className="mt-3 flex items-center text-xs text-gray-500">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Last updated: {new Date(visa.updated_at).toLocaleDateString()}
                </div>
              </div>

              {/* Contact Button */}
              <div className="mt-8 pt-6 border-t border-gray-100">
                <button className="w-full inline-flex items-center justify-center px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 font-bold shadow-lg hover:shadow-xl transform hover:scale-105">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Get Expert Guidance
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisaDetailsPage;
